import { assert, logger, parseColor } from '@fe/core';
import { mercatorUtils } from '@fe/maplibre-extensions';
import { Color } from '@maplibre/maplibre-gl-style-spec';
import { CustomLayerInterface, CustomRenderMethodInput, Map as MaplibreMap } from 'maplibre-gl';
import { Palette } from '../../../palettes/palettes';
import { BarbColorType } from '../../../settings/settings';
import fragmentSource from './symbol.fragment.glsl?raw';
import vertexSource from './symbol.vert.glsl?raw';

type ZoomExpression = number | ((zoom: number) => number);

export class SymbolLayer implements CustomLayerInterface {
    readonly type = 'custom';
    readonly renderingMode?: '2d';
    private shaderMap = new Map<string, WebGLProgram>();
    private gl: WebGL2RenderingContext | null = null;
    private aPos: GLint = 0; // TODO: use VAO
    private aCenter: GLint = 0; // TODO: use VAO
    private aTexCoords: GLint = 0; // TODO: use VAO
    private aTexOffset: GLint = 0; // TODO: use VAO
    private aRot: GLint = 0; // TODO: use VAO
    private aAlpha: GLint = 0; // TODO: use VAO
    private aColor: GLint = 0;

    private vertexBuffer: WebGLBuffer | null = null;
    private centerBuffer: WebGLBuffer | null = null;
    private rotationMatrices: WebGLBuffer | null = null;
    private texCoordsBuffer: WebGLBuffer | null = null;
    private texOffsetsBuffer: WebGLBuffer | null = null;
    private alphaBuffer: WebGLBuffer | null = null;
    private colorBuffer: WebGLBuffer | null = null;
    private texture: WebGLTexture | null = null;
    private map: MaplibreMap | null = null;
    private instanceCount = 0;
    private _size: ZoomExpression = 24;
    private _opacity = 1.0;
    private imageWidth = 0;
    private imageHeight = 0;
    private imgsCount = 0;
    private barbsColorType: BarbColorType = BarbColorType.auto;
    private barbsColor: [number, number, number, number] = [0, 0, 0, 0];

    constructor(public readonly id: string) {}

    setBarbsColorType(type: BarbColorType) {
        this.barbsColorType = type;
        this.map?.triggerRepaint();
    }

    setBarbsColor(value: string | null) {
        if (!value) {
            return;
        }
        const color = Color.parse(value);
        this.barbsColor = [color!.r, color!.g, color!.b, color!.a];
        this.map?.triggerRepaint();
    }

    get opacity(): number {
        return this._opacity;
    }

    set opacity(value: number) {
        this._opacity = value;
        this.map?.triggerRepaint();
    }

    get size() {
        return this._size;
    }

    set size(value: ZoomExpression) {
        this._size = value;
        this.map?.triggerRepaint();
    }

    private getShader(gl: WebGLRenderingContext | WebGL2RenderingContext, shaderDescription: CustomRenderMethodInput['shaderData']) {
        // Pick a shader based on the current projection, defined by `variantName`.
        if (this.shaderMap.has(shaderDescription.variantName)) {
            return this.shaderMap.get(shaderDescription.variantName);
        }

        const vs = vertexSource
            .replace('__define__', shaderDescription.define)
            .replace('__prelude__', shaderDescription.vertexShaderPrelude);

        // create a vertex shader
        const vertexShader = gl.createShader(gl.VERTEX_SHADER)!;
        gl.shaderSource(vertexShader, vs);
        gl.compileShader(vertexShader);
        if (!gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS)) {
            console.error(gl.getShaderInfoLog(vertexShader));
        }

        // create a fragment shader
        const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER)!;
        gl.shaderSource(fragmentShader, fragmentSource);
        gl.compileShader(fragmentShader);
        if (!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS)) {
            console.error(gl.getShaderInfoLog(fragmentShader));
        }

        // link the two shaders into a WebGL program
        const program = gl.createProgram();
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);

        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            console.error(gl.getProgramInfoLog(program));
        }

        this.aPos = gl.getAttribLocation(program, 'a_pos');
        this.aCenter = gl.getAttribLocation(program, 'a_center');
        this.aTexCoords = gl.getAttribLocation(program, 'a_tex_coords');
        this.aRot = gl.getAttribLocation(program, 'a_rot');
        this.aTexOffset = gl.getAttribLocation(program, 'a_tex_offset');
        this.aAlpha = gl.getAttribLocation(program, 'a_alpha');
        this.aColor = gl.getAttribLocation(program, 'a_color');

        this.shaderMap.set(shaderDescription.variantName, program);

        return program;
    }

    setData<T>(
        items: Array<T & { position: number[]; rotate: number; alpha: number; speed: number | null }>,
        getImage: (row: T) => string | null,
        palette: Palette
    ) {
        if (!this.gl) {
            return;
        }
        const gl = this.gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, 1, 1, 1, -1, -1, 1, -1]), gl.STATIC_DRAW);
        this.instanceCount = items.length;

        gl.bindBuffer(gl.ARRAY_BUFFER, this.centerBuffer);
        gl.bufferData(
            gl.ARRAY_BUFFER,
            new Float32Array(
                items
                    .map((x) => {
                        const pos = mercatorUtils.mercatorFromLngLat(x.position);
                        return [pos[0], pos[1]];
                    })
                    .flat()
            ),
            gl.STATIC_DRAW
        );

        gl.bindBuffer(gl.ARRAY_BUFFER, this.rotationMatrices);
        gl.bufferData(
            gl.ARRAY_BUFFER,
            new Float32Array(
                items
                    .map((x) => {
                        const a = (-x.rotate * Math.PI) / 180;
                        return [Math.cos(a), Math.sin(a), -Math.sin(a), Math.cos(a)];
                    })
                    .flat()
            ),
            gl.STATIC_DRAW
        );

        gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordsBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 1, 1, 1, 0, 0, 1, 0]), gl.STATIC_DRAW);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.alphaBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(items.map((x) => x.alpha / 255.0)), gl.STATIC_DRAW);

        const colorGetter = getWindSpeedColor(palette);
        gl.bindBuffer(gl.ARRAY_BUFFER, this.colorBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(items.map((x) => colorGetter(x.speed ?? -1)).flat()), gl.STATIC_DRAW);

        const imageNames = items.map((x) => getImage(x));
        const set = new Set<string>();
        imageNames.forEach((x) => x && set.add(x));
        const imageNamesUnique = [...set];
        const offsets = new Uint8Array(items.length);

        for (let i = 0; i < items.length; i++) {
            if (imageNames[i]) {
                offsets[i] = imageNamesUnique.indexOf(imageNames[i]!);
            }
        }
        gl.bindBuffer(gl.ARRAY_BUFFER, this.texOffsetsBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, offsets, gl.STATIC_DRAW);

        const images = imageNamesUnique.map((x) => this.map!.getImage(x));
        if (images.some((x) => !x)) {
            logger.warn('some images not found');
            return;
        }

        const imgs = images.filter(Boolean);
        if (imgs.some((x) => x.data.width !== imgs[0].data.width || x.data.height !== imgs[0].data.height)) {
            logger.warn('All images must be the same size');
            return;
        }

        if (imgs.length === 0) {
            return;
        }

        const w = imgs[0].data.width;
        const h = imgs[1].data.height;

        const wh = w * h;
        const bitmap = new Uint8Array(wh * imgs.length * 4);
        for (let i = 0; i < imgs.length; i++) {
            const img = imgs[i].data.data;
            for (let j = 0; j < img.length; j++) {
                bitmap[wh * i * 4 + j] = img[j];
            }
        }
        this.imageWidth = w;
        this.imageHeight = h;
        this.imgsCount = imgs.length;

        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h * imgs.length, 0, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        this.map?.triggerRepaint();
    }

    onAdd(map: MaplibreMap, gl: WebGL2RenderingContext): void {
        this.map = map;
        this.gl = gl;
        this.vertexBuffer = gl.createBuffer();
        this.centerBuffer = gl.createBuffer();
        this.texCoordsBuffer = gl.createBuffer();
        this.rotationMatrices = gl.createBuffer();
        this.texOffsetsBuffer = gl.createBuffer();
        this.alphaBuffer = gl.createBuffer();
        this.colorBuffer = gl.createBuffer();
        this.texture = gl.createTexture();
    }

    onRemove(map: MaplibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
        this.map = null;
        gl.deleteBuffer(this.vertexBuffer);
        gl.deleteBuffer(this.centerBuffer);
        gl.deleteBuffer(this.texCoordsBuffer);
        gl.deleteBuffer(this.rotationMatrices);
        gl.deleteBuffer(this.texOffsetsBuffer);
        gl.deleteBuffer(this.alphaBuffer);
        gl.deleteBuffer(this.colorBuffer);
        gl.deleteTexture(this.texture);

        gl.disableVertexAttribArray(this.aPos);
        gl.disableVertexAttribArray(this.aCenter);
        gl.disableVertexAttribArray(this.aTexCoords);
        gl.disableVertexAttribArray(this.aTexOffset);
        gl.disableVertexAttribArray(this.aRot);
        gl.disableVertexAttribArray(this.aAlpha);
        gl.disableVertexAttribArray(this.aColor);

        this.vertexBuffer = null;
        this.centerBuffer = null;
        this.texCoordsBuffer = null;
        this.rotationMatrices = null;
        this.texOffsetsBuffer = null;
        this.alphaBuffer = null;
        this.colorBuffer = null;
        this.texture = null;

        for (const program of this.shaderMap.values()) {
            gl.deleteProgram(program);
        }

        this.shaderMap.clear();
        this.gl = null;
    }

    render(_gl: WebGLRenderingContext | WebGL2RenderingContext, args: CustomRenderMethodInput) {
        if (!this.map) {
            return;
        }
        const gl = _gl as WebGL2RenderingContext;
        const globe = args.shaderData.variantName === 'globe';
        (globe ? [0] : this.getWorldOffsets()).forEach((offset) => {
            const program = this.getShader(gl, args.shaderData)!;
            gl.enable(gl.BLEND);
            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.SRC_ALPHA, gl.DST_ALPHA);

            gl.useProgram(program);
            gl.uniformMatrix4fv(
                gl.getUniformLocation(program, 'u_projection_fallback_matrix'),
                false,
                args.defaultProjectionData.fallbackMatrix // convert mat4 from gl-matrix to a plain array
            );
            gl.uniformMatrix4fv(
                gl.getUniformLocation(program, 'u_projection_matrix'),
                false,
                args.defaultProjectionData.mainMatrix // convert mat4 from gl-matrix to a plain array
            );
            gl.uniform4f(
                gl.getUniformLocation(program, 'u_projection_tile_mercator_coords'),
                ...args.defaultProjectionData.tileMercatorCoords
            );

            gl.uniform4f(gl.getUniformLocation(program, 'u_projection_clipping_plane'), ...args.defaultProjectionData.clippingPlane);
            gl.uniform1f(gl.getUniformLocation(program, 'u_projection_transition'), args.defaultProjectionData.projectionTransition);
            gl.uniform1f(gl.getUniformLocation(program, 'u_worldXOffset'), offset);
            gl.uniform1f(gl.getUniformLocation(program, 'u_opacity'), this.opacity);

            const zoom = this.map!.getZoom();
            const radius = (typeof this._size === 'function' ? this._size(zoom) : this._size) / 2;
            const extrude = pixelsToTileUnits(radius, zoom);
            gl.uniform1f(gl.getUniformLocation(program, 'u_radius'), radius);
            gl.uniform2f(gl.getUniformLocation(program, 'u_extrude_scale'), extrude, extrude);
            gl.uniform2f(gl.getUniformLocation(program, 'u_image_size'), this.imageWidth, this.imageHeight);
            gl.uniform1f(gl.getUniformLocation(program, 'u_images_count'), this.imgsCount);
            gl.uniform1i(gl.getUniformLocation(program, 'u_color_type'), this.barbsColorType);
            gl.uniform4f(gl.getUniformLocation(program, 'u_color_fixed'), ...this.barbsColor);

            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, this.texture);
            gl.uniform1i(gl.getUniformLocation(program, 'u_texture'), 0);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.centerBuffer);
            gl.enableVertexAttribArray(this.aCenter);
            gl.vertexAttribPointer(this.aCenter, 2, gl.FLOAT, false, 0, 0);
            gl.vertexAttribDivisor(this.aCenter, 1);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordsBuffer);
            gl.enableVertexAttribArray(this.aTexCoords);
            gl.vertexAttribPointer(this.aTexCoords, 2, gl.FLOAT, false, 0, 0);
            gl.vertexAttribDivisor(this.aTexCoords, 0);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.rotationMatrices);
            gl.enableVertexAttribArray(this.aRot);
            gl.vertexAttribPointer(this.aRot, 2, gl.FLOAT, false, 16, 0);
            gl.vertexAttribDivisor(this.aRot, 1);

            gl.enableVertexAttribArray(this.aRot + 1);
            gl.vertexAttribPointer(this.aRot + 1, 2, gl.FLOAT, false, 16, 8);
            gl.vertexAttribDivisor(this.aRot + 1, 1);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.texOffsetsBuffer);
            gl.enableVertexAttribArray(this.aTexOffset);
            gl.vertexAttribPointer(this.aTexOffset, 1, gl.UNSIGNED_BYTE, false, 0, 0);
            gl.vertexAttribDivisor(this.aTexOffset, 1);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.alphaBuffer);
            gl.enableVertexAttribArray(this.aAlpha);
            gl.vertexAttribPointer(this.aAlpha, 1, gl.FLOAT, false, 0, 0);
            gl.vertexAttribDivisor(this.aAlpha, 1);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.colorBuffer);
            gl.enableVertexAttribArray(this.aColor);
            gl.vertexAttribPointer(this.aColor, 4, gl.FLOAT, false, 0, 0);
            gl.vertexAttribDivisor(this.aColor, 1);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
            gl.enableVertexAttribArray(this.aPos);
            gl.vertexAttribPointer(this.aPos, 2, gl.FLOAT, false, 0, 0);
            gl.vertexAttribDivisor(this.aPos, 0);

            gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.instanceCount);
        });
        gl.vertexAttribDivisor(this.aCenter, 0);
        gl.vertexAttribDivisor(this.aTexCoords, 0);
        gl.vertexAttribDivisor(this.aRot, 1);
        gl.vertexAttribDivisor(this.aRot + 1, 0);
        gl.vertexAttribDivisor(this.aTexOffset, 0);
        gl.vertexAttribDivisor(this.aAlpha, 0);
        gl.vertexAttribDivisor(this.aColor, 0);
        gl.vertexAttribDivisor(this.aPos, 0);
    }

    private getWorldOffsets() {
        const bounds = this.map!.getBounds();
        const west = bounds.getWest();
        const east = bounds.getEast();

        const x1 = 0 - Math.ceil((Math.abs(west) - 180) / 360);
        const x2 = Math.ceil((Math.abs(east) - 180) / 360);

        const worldOffsets = [];
        for (let i = x1; i <= x2; i++) {
            worldOffsets.push(i);
        }
        return worldOffsets;
    }
}

function getWindSpeedColor(palette: Palette) {
    assert(palette.type === 'gradientBar');

    const stops = palette.stops.map((x) => ({
        value: x.value,
        color: parseColor(x.color)
    }));

    return (speed: number) => {
        if (speed < palette.minValue) {
            return stops[0].color;
        }

        for (const stop of stops) {
            if (speed < stop.value) {
                return stop.color;
            }
        }

        return stops[palette.stops.length - 1].color;
    };
}

export function pixelsToTileUnits(pixelValue: number, z: number): number {
    return pixelValue * (1.0 / (512 * Math.pow(2, z)));
}
